import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl, normalizeExternalUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import CartWidget from "@/components/CartWidget";
import { useCart, canCart } from "@/lib/cart";
import { waLink, offerMessage, timeLeft, shareLink, imgFallback } from "@/lib/offerUtils";
import { MessageCircle, Share2, ArrowLeft, Tag, Loader2, Store, Phone, ExternalLink, X } from "lucide-react";

export default function OfferDetail({ slugOverride }) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const offerId = params.offerId;
  const home = slugOverride ? "/" : `/b/${slug}`;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(false);
  const cart = useCart(slug);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/public/offer/${offerId}`);
        setData(res.data);
      } catch {
        setNotFound(true);
      }
    })();
  }, [offerId]);

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-5">
      <Tag className="w-12 h-12 text-primary" />
      <h1 className="font-display text-2xl font-bold">Offer not found</h1>
      <Link to={home}><Button className="rounded-full bg-primary hover:bg-secondary">Back to page</Button></Link>
    </div>
  );
  if (!data) return (<div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>);

  const { offer: o, business: b } = data;
  const imgs = o.images?.length ? o.images : (o.image_path ? [o.image_path] : []);

  return (
    <div className="min-h-screen" data-public-theme={b?.theme}>
      <div className="max-w-3xl mx-auto px-5 py-8">
        <button onClick={() => navigate(home)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-white mb-6" data-testid="offer-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to {b?.name}
        </button>

        <div className="rounded-2xl overflow-hidden border border-white/10">
          <div className="bg-secondary/20 relative">
            {imgs.length ? (
              <img src={fileUrl(imgs[idx])} alt={o.title} onClick={() => setZoom(true)} onError={imgFallback} className="w-full max-h-[70vh] object-contain cursor-zoom-in bg-black/40" data-testid="offer-main-image" />
            ) : <div className="aspect-video flex items-center justify-center"><Tag className="w-10 h-10 text-primary/40" /></div>}
            {o.badge && <Badge className="absolute top-3 left-3 bg-primary">{o.badge}</Badge>}
          </div>
          {imgs.length > 1 && (
            <div className="flex gap-2 p-3 overflow-x-auto">
              {imgs.map((p, i) => (
                <img key={p + i} src={fileUrl(p)} alt="" onClick={() => setIdx(i)} className={`w-16 h-16 rounded-md object-cover cursor-pointer border-2 ${i === idx ? "border-primary" : "border-transparent opacity-70"}`} />
              ))}
            </div>
          )}
          <div className="p-6">
            <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="offer-detail-title">{o.title}</h1>
            <div className="flex items-center gap-3 mt-2">
              {o.discount && <span className="text-primary font-bold text-xl">{o.discount}</span>}
              {o.price && <span className="text-muted-foreground">{o.price}</span>}
            </div>
            {o.description && <p className="text-muted-foreground mt-4">{o.description}</p>}
            {o.terms && (
              <div className="mt-6 bg-card border border-white/10 rounded-xl p-4">
                <span className="overline text-muted-foreground">Terms & details</span>
                <p className="text-sm mt-2 whitespace-pre-wrap">{o.terms}</p>
              </div>
            )}
            {timeLeft(o.expires_at) && <p className="mt-3 text-sm text-yellow-400">⏱ {timeLeft(o.expires_at)}</p>}
            {o.sold_out && <p className="mt-3 text-sm font-semibold text-red-400">Sold out</p>}
            <div className="mt-6 flex flex-wrap gap-3">
              {b?.whatsapp && !o.sold_out && (
                <a href={waLink(b.whatsapp, offerMessage(b.name, o, window.location.href))} target="_blank" rel="noreferrer" onClick={() => api.post(`/public/${slug}/event`, { type: "whatsapp", offer_id: o.offer_id }).catch(() => {})}>
                  <Button className="rounded-full bg-green-600 hover:bg-green-700" data-testid="order-whatsapp-btn"><MessageCircle className="w-4 h-4 mr-1" /> Order on WhatsApp</Button>
                </a>
              )}
              {canCart(b) && !o.sold_out && (
                <Button variant="outline" className="rounded-full border-primary/50 text-primary" data-testid="add-to-cart-btn" onClick={() => { cart.add(o); toast.success("Added to cart"); }}>+ Add to cart</Button>
              )}
              <Button variant="outline" className="rounded-full border-white/15" data-testid="share-offer-btn" onClick={async () => { const r = await shareLink(o.title, window.location.href); if (r === "copied") toast.success("Link copied"); }}>
                <Share2 className="w-4 h-4 mr-1" /> Share
              </Button>
            </div>
            {o.external_url && (
              <a href={normalizeExternalUrl(o.external_url)} target="_blank" rel="noreferrer">
                <Button className="mt-6 rounded-full bg-primary hover:bg-secondary" data-testid="offer-external-btn">
                  Claim this offer <ExternalLink className="w-4 h-4 ml-1" />
                </Button>
              </a>
            )}
          </div>
        </div>

        <CartWidget slug={slug} business={b} pageUrl={slugOverride ? window.location.origin : `${window.location.origin}/b/${slug}`} trackPath={(id) => (slugOverride ? `/order/${id}` : `/b/${slug}/order/${id}`)} />

        {zoom && imgs.length > 0 && (
          <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-2" onClick={() => setZoom(false)} data-testid="image-lightbox">
            <button className="absolute top-4 right-4 bg-white/10 rounded-full p-2" onClick={() => setZoom(false)}><X className="w-5 h-5" /></button>
            <img src={fileUrl(imgs[idx])} alt={o.title} className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
            {imgs.length > 1 && (
              <div className="absolute bottom-4 flex gap-2" onClick={(e) => e.stopPropagation()}>
                {imgs.map((_, i) => <button key={i} onClick={() => setIdx(i)} className={`w-2.5 h-2.5 rounded-full ${i === idx ? "bg-primary" : "bg-white/40"}`} />)}
              </div>
            )}
          </div>
        )}

        {b && (
          <div className="mt-6 bg-card border border-white/10 rounded-xl p-5 flex items-center gap-4">
            {b.logo_path ? <img src={fileUrl(b.logo_path)} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 rounded-lg bg-secondary/40 flex items-center justify-center"><Store className="w-5 h-5 text-primary" /></div>}
            <div className="flex-1">
              <p className="font-display font-semibold">{b.name}</p>
              {b.phone && <p className="text-sm text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" />{b.phone}</p>}
            </div>
            <Link to={home}><Button variant="outline" size="sm" className="rounded-full border-white/15" data-testid="offer-view-shop-btn">View shop</Button></Link>
          </div>
        )}
      </div>
    </div>
  );
}
